import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../storeConfig/store';

import {
	SUSTAINABILITY_ANSERQUESTION as SUSTAINABILITY_ANSWER_QUESTION,
	SUSTAINABILITY_SAVE_ANSWERS,
	SUSTAINABILITY_DELETE_FORMS,
	SUSTAINABILITY_UPDATE_DOCUMENT_FILENAME
} from './action.types';
import { answerQuestion } from '../slices';
import { SustainabilityFormUpdateRequestDto } from '../../models/sustainability';
import sustainabilityApi from '../apis/sustainability.api';
import { clearFormsByProfileId } from '../slices/sustainability-forms.slice';
import { syncFormAnswerSectionStatus } from './sustainability-section-status';

export const sustainabilityListener = createListenerMiddleware();

const saveAnswersInternal = async (
	profileId: number,
	formId: number,
	dispatch: AppDispatch,
	getState: () => RootState
) => {
	const state = getState();

	const answers = state.sustainabilityForms?.profileForms?.[profileId]?.[formId]?.answers ?? [];

	const request: SustainabilityFormUpdateRequestDto = {
		answers,
		formId
	};

	await dispatch(sustainabilityApi.endpoints.updateForm.initiate({ profileId, request })).unwrap();

	await dispatch({
		type: 'SET_TOAST',
		data: { title: 'Draft saved', show: true, showSuccessTick: true }
	});
};

export const saveSustainabilityAnswers =
	(profileId: number, formId: number) =>
	async (dispatch: AppDispatch, getState: () => RootState) => {
		await saveAnswersInternal(profileId, formId, dispatch, getState);
	};

const deleteFormsInternal = async (profileId: number, dispatch: AppDispatch) => {
	await dispatch(
		sustainabilityApi.endpoints.softDeleteFormsByProfileId.initiate(profileId)
	).unwrap();

	await dispatch(
		sustainabilityApi.util.invalidateTags([
			{ type: 'sustainability', id: profileId },
			{ type: 'sustainability', id: 'LIST' }
		])
	);

	await dispatch(clearFormsByProfileId({ profileId }));

	await dispatch({
		type: 'SET_TOAST',
		data: { title: 'Forms deleted', show: true, showSuccessTick: true }
	});
};

export const deleteSustainabilityForms = (profileId: number) => async (dispatch: AppDispatch) => {
	await deleteFormsInternal(profileId, dispatch);
};

const updateDocumentFilenameInternal = async (
	profileId: number,
	formId: number,
	documentId: number,
	fileName: string,
	dispatch: AppDispatch
) => {
	await dispatch(
		sustainabilityApi.endpoints.updateSupportingDocumentFilename.initiate({
			profileId,
			request: { formId, documentId, fileName }
		})
	).unwrap();
};

export const updateSustainabilityDocumentFilename =
	(profileId: number, formId: number, documentId: number, fileName: string) =>
	async (dispatch: AppDispatch) => {
		await updateDocumentFilenameInternal(profileId, formId, documentId, fileName, dispatch);
	};

// When SUSTAINABILITY_ANSERQUESTION is dispatched (e.g. from SignalR or external source),
// this listener dispatches answerQuestion so the form state and answerSections stay in sync.
sustainabilityListener.startListening({
	predicate: (action) => action.type === SUSTAINABILITY_ANSWER_QUESTION,
	effect: async (action, { dispatch, getState }) => {
		const { profileId, formId, questionId, answer, profileType, section } = action.payload ?? {};
		if (profileId !== null && formId !== null && questionId !== null && answer !== undefined) {
			dispatch(answerQuestion({ profileId, formId, questionId, answer }));

			const state = getState() as RootState;

			if (profileType !== null && section !== null) {
				syncFormAnswerSectionStatus(
					dispatch as AppDispatch,
					state,
					profileId,
					formId,
					profileType,
					section
				);
			}
		}
	}
});

sustainabilityListener.startListening({
	predicate: (action) => action.type === SUSTAINABILITY_SAVE_ANSWERS,
	effect: async (action, { dispatch, getState }) => {
		const { formId, profileId } = action.payload ?? {};

		if (profileId === null || formId === null) return;

		await saveAnswersInternal(
			profileId,
			formId,
			dispatch as AppDispatch,
			getState as () => RootState
		);
	}
});

sustainabilityListener.startListening({
	predicate: (action) => action.type === SUSTAINABILITY_DELETE_FORMS,
	effect: async (action, { dispatch }) => {
		const { profileId } = action.payload ?? {};

		if (profileId === null) return;

		await deleteFormsInternal(profileId, dispatch as AppDispatch);
	}
});

sustainabilityListener.startListening({
	predicate: (action) => action.type === SUSTAINABILITY_UPDATE_DOCUMENT_FILENAME,
	effect: async (action, { dispatch }) => {
		const { profileId, formId, documentId, fileName } = action.payload ?? {};

		if (
			profileId === null ||
			formId === null ||
			documentId === null ||
			fileName === null ||
			fileName.trim() === ''
		)
			return;

		await updateDocumentFilenameInternal(
			profileId,
			formId,
			documentId,
			fileName.trim(),
			dispatch as AppDispatch
		);
	}
});
